import React, { useState, useMemo } from 'react';
import { 
  Card, 
  Table, 
  Typography, 
  Space, 
  Button, 
  Modal, 
  Tag, 
  Tooltip, 
  Statistic, 
  Row, 
  Col,
  Progress,
  message,
  Tabs,
  Divider
} from 'antd';
import { 
  EyeOutlined, 
  CopyOutlined, 
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { ResultComponentProps } from '../common/types';
import BaseResultDisplay from '../common/BaseResultDisplay';

const { Text, Paragraph, Title } = Typography;
const { TabPane } = Tabs;

// 批量结果类型定义
interface BatchResultItem {
  id: string;
  [key: string]: any;
}

interface BatchResultData {
  count: number;
  items: BatchResultItem[];
  result_type?: 'validation' | 'conversion' | 'translation';
}

// 序列详情模态框
const SequenceDetailModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  data: any;
  type: string;
}> = ({ visible, onClose, data, type }) => {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('已复制到剪贴板');
  };

  const renderValidationDetail = () => (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Statistic 
            title="序列长度" 
            value={data.statistics?.length || 0} 
          />
        </Col>
        <Col span={12}>
          <Statistic 
            title="GC含量" 
            value={data.statistics?.gc_content || 0} 
            suffix="%"
            precision={2}
          />
        </Col>
      </Row>
      
      <Divider>序列统计</Divider>
      <Row gutter={16}>
        <Col span={6}>
          <Statistic title="A" value={data.statistics?.A || 0} />
        </Col>
        <Col span={6}>
          <Statistic title="T" value={data.statistics?.T || 0} />
        </Col>
        <Col span={6}>
          <Statistic title="G" value={data.statistics?.G || 0} />
        </Col>
        <Col span={6}>
          <Statistic title="C" value={data.statistics?.C || 0} />
        </Col>
      </Row>
      
      {data.filtered_sequence && (
        <div style={{ marginTop: 16 }}>
          <Divider>过滤后序列</Divider>
          <Paragraph 
            copyable={{ onCopy: () => copyToClipboard(data.filtered_sequence) }}
            style={{ 
              fontFamily: 'monospace', 
              backgroundColor: '#f5f5f5', 
              padding: 8,
              wordBreak: 'break-all'
            }}
          >
            {data.filtered_sequence}
          </Paragraph>
        </div>
      )}
    </div>
  );

  const renderConversionDetail = () => (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card size="small" title="原始序列">
            <Tag color="blue">{data.from_type?.toUpperCase()}</Tag>
            <Paragraph 
              copyable={{ onCopy: () => copyToClipboard(data.original_sequence) }}
              style={{ 
                fontFamily: 'monospace', 
                backgroundColor: '#f5f5f5', 
                padding: 8,
                wordBreak: 'break-all',
                marginTop: 8
              }}
            >
              {data.original_sequence}
            </Paragraph>
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" title="转换后序列">
            <Tag color="green">{data.to_type?.toUpperCase()}</Tag>
            <Paragraph 
              copyable={{ onCopy: () => copyToClipboard(data.converted_sequence) }}
              style={{ 
                fontFamily: 'monospace', 
                backgroundColor: '#f5f5f5', 
                padding: 8,
                wordBreak: 'break-all',
                marginTop: 8
              }}
            >
              {data.converted_sequence}
            </Paragraph>
          </Card>
        </Col>
      </Row>
    </div>
  );

  const renderTranslationDetail = () => {
    const translations = data.translations || {};
    return (
      <div>
        <Card size="small" title="原始序列" style={{ marginBottom: 16 }}>
          <Tag color="blue">{data.from_type?.toUpperCase()}</Tag>
          <Paragraph 
            copyable={{ onCopy: () => copyToClipboard(data.original_sequence) }}
            style={{ 
              fontFamily: 'monospace', 
              backgroundColor: '#f5f5f5', 
              padding: 8,
              wordBreak: 'break-all',
              marginTop: 8
            }}
          >
            {data.original_sequence}
          </Paragraph>
        </Card>
        
        <Divider>六框翻译结果</Divider>
        <Tabs defaultActiveKey="frame1">
          {Object.entries(translations).map(([frame, sequence]) => (
            <TabPane tab={`${frame.toUpperCase()}`} key={frame}>
              <Paragraph 
                copyable={{ onCopy: () => copyToClipboard(sequence as string) }}
                style={{ 
                  fontFamily: 'monospace', 
                  backgroundColor: '#f5f5f5', 
                  padding: 8,
                  wordBreak: 'break-all'
                }}
              >
                {sequence as string}
              </Paragraph>
            </TabPane>
          ))}
        </Tabs>
      </div>
    );
  };

  // 添加空值检查
  if (!data) {
    return null;
  }

  return (
    <Modal
      title={`序列详情 - ${data.id || '未知'}`}
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="close" onClick={onClose}>
          关闭
        </Button>
      ]}
    >
      {type === 'validation' && renderValidationDetail()}
      {type === 'conversion' && renderConversionDetail()}
      {type === 'translation' && renderTranslationDetail()}
    </Modal>
  );
};

// 主组件
const BatchSequenceResult: React.FC<ResultComponentProps> = ({
  data,
  title,
  onDeleteResult,
  multiResults = false,
  resultIndex,
}) => {
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState<any>(null);

  // 添加调试日志
  console.log('BatchSequenceResult 接收到的数据:', data);
  console.log('data.results:', data?.results);
  console.log('data.items:', data?.items);

  // 确定结果类型
  const resultType = useMemo(() => {
    if (data?.result_type) return data.result_type;
    
    // 根据数据结构推断类型
    let firstItem;
    
    // 处理新的统一结果格式
    if (data?.results && Array.isArray(data.results) && data.results.length > 0) {
      firstItem = data.results[0].result;
    } else {
      // 处理旧的 count + items 格式
      firstItem = data?.items?.[0];
    }
    
    if (!firstItem) return 'unknown';
    
    if (firstItem.is_valid !== undefined) return 'validation';
    if (firstItem.converted_sequence !== undefined) return 'conversion';
    if (firstItem.translations !== undefined) return 'translation';
    
    return 'unknown';
  }, [data]);

  // 处理查看详情
  const handleViewDetail = (item: any) => {
    setCurrentItem(item);
    setDetailModalVisible(true);
  };

  // 验证结果表格列
  const validationColumns = [
    {
      title: '序列ID',
      dataIndex: 'id',
      key: 'id',
      width: 150,
      ellipsis: true,
    },
    {
      title: '验证状态',
      dataIndex: 'is_valid',
      key: 'is_valid',
      width: 100,
      render: (isValid: boolean) => (
        <Tag 
          color={isValid ? 'success' : 'error'} 
          icon={isValid ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        >
          {isValid ? '有效' : '无效'}
        </Tag>
      ),
    },
    {
      title: '检测类型',
      dataIndex: 'detected_type',
      key: 'detected_type',
      width: 100,
      render: (type: string) => (
        <Tag color="blue">{type?.toUpperCase()}</Tag>
      ),
    },
    {
      title: '序列长度',
      key: 'length',
      width: 100,
      render: (record: any) => record.statistics?.length || 0,
    },
    {
      title: 'GC含量',
      key: 'gc_content',
      width: 100,
      render: (record: any) => (
        <span>{(record.statistics?.gc_content || 0).toFixed(2)}%</span>
      ),
    },
    {
      title: '消息',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (record: any) => (
        <Button 
          type="text" 
          icon={<EyeOutlined />} 
          onClick={() => handleViewDetail(record)}
        />
      ),
    },
  ];

  // 转换结果表格列
  const conversionColumns = [
    {
      title: '序列ID',
      dataIndex: 'id',
      key: 'id',
      width: 150,
      ellipsis: true,
    },
    {
      title: '原始类型',
      dataIndex: 'from_type',
      key: 'from_type',
      width: 100,
      render: (type: string) => (
        <Tag color="blue">{type?.toUpperCase()}</Tag>
      ),
    },
    {
      title: '转换类型',
      dataIndex: 'to_type',
      key: 'to_type',
      width: 100,
      render: (type: string) => (
        <Tag color="green">{type?.toUpperCase()}</Tag>
      ),
    },
    {
      title: '原始序列',
      dataIndex: 'original_sequence',
      key: 'original_sequence',
      render: (text: string) => (
        <Text 
          style={{ fontFamily: 'monospace' }}
          ellipsis={{ tooltip: text }}
        >
          {text?.length > 30 ? `${text.substring(0, 30)}...` : text}
        </Text>
      ),
    },
    {
      title: '转换序列',
      dataIndex: 'converted_sequence',
      key: 'converted_sequence',
      render: (text: string) => (
        <Text 
          style={{ fontFamily: 'monospace' }}
          ellipsis={{ tooltip: text }}
        >
          {text?.length > 30 ? `${text.substring(0, 30)}...` : text}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (record: any) => (
        <Button 
          type="text" 
          icon={<EyeOutlined />} 
          onClick={() => handleViewDetail(record)}
        />
      ),
    },
  ];

  // 翻译结果表格列
  const translationColumns = [
    {
      title: '序列ID',
      dataIndex: 'id',
      key: 'id',
      width: 150,
      ellipsis: true,
    },
    {
      title: '原始类型',
      dataIndex: 'from_type',
      key: 'from_type',
      width: 100,
      render: (type: string) => (
        <Tag color="blue">{type?.toUpperCase()}</Tag>
      ),
    },
    {
      title: '翻译框数',
      key: 'frame_count',
      width: 100,
      render: (record: any) => (
        <span>{Object.keys(record.translations || {}).length}</span>
      ),
    },
    {
      title: '原始序列',
      dataIndex: 'original_sequence',
      key: 'original_sequence',
      render: (text: string) => (
        <Text 
          style={{ fontFamily: 'monospace' }}
          ellipsis={{ tooltip: text }}
        >
          {text?.length > 30 ? `${text.substring(0, 30)}...` : text}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (record: any) => (
        <Button 
          type="text" 
          icon={<EyeOutlined />} 
          onClick={() => handleViewDetail(record)}
        />
      ),
    },
  ];

  // 根据类型选择列配置
  const getColumns = () => {
    switch (resultType) {
      case 'validation':
        return validationColumns;
      case 'conversion':
        return conversionColumns;
      case 'translation':
        return translationColumns;
      default:
        return [];
    }
  };

  // 获取结果类型标题
  const getResultTypeTitle = () => {
    switch (resultType) {
      case 'validation':
        return '批量序列验证结果';
      case 'conversion':
        return '批量序列转换结果';
      case 'translation':
        return '批量序列翻译结果';
      default:
        return '批量处理结果';
    }
  };

  // 根据结果类型获取表格数据
  const getTableData = () => {
    // 处理新的统一结果格式
    if (data?.results && Array.isArray(data.results)) {
      return data.results.map((item: any, index: number) => ({
        key: index,
        id: item.sequence_id || `seq_${index + 1}`,
        ...item.result, // 展开实际结果数据
        success: item.success,
        error: item.error,
      }));
    }
    
    // 处理旧的 count + items 格式
    const items = data?.items || [];
    
    if (resultType === 'validation') {
      return items.map((item: BatchResultItem, index: number) => ({
        key: index,
        ...item,
        id: item.id || `seq_${index + 1}`,
      }));
    }
    
    return items.map((item: any, index: number) => ({
      key: index,
      ...item,
      id: item.id || `seq_${index + 1}`,
    }));
  };

  // 统计信息
  const getStatistics = () => {
    // 处理新的统一结果格式
    if (data?.results && Array.isArray(data.results)) {
      const items = getTableData();
      const total = items.length;
      
      if (resultType === 'validation') {
        const valid = items.filter((item: any) => item.is_valid || item.success).length;
        const invalid = total - valid;
        return { total, valid, invalid };
      }
      
      return { total };
    }
    
    // 处理旧的 count + items 格式
    const items = data?.items || [];
    const total = Array.isArray(items) ? items.length : (data?.count || 0);
    
    if (resultType === 'validation') {
      const valid = Array.isArray(items) ? items.filter((item: BatchResultItem) => item.is_valid).length : 0;
      const invalid = total - valid;
      return { total, valid, invalid };
    }
    
    return { total };
  };

  const stats = getStatistics();

  const content = (
    <div>
      {/* 统计信息 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Statistic title="总序列数" value={stats.total} />
        </Col>
        {resultType === 'validation' && (
          <>
            <Col span={8}>
              <Statistic 
                title="有效序列" 
                value={stats.valid} 
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col span={8}>
              <Statistic 
                title="无效序列" 
                value={stats.invalid} 
                valueStyle={{ color: '#cf1322' }}
              />
            </Col>
          </>
        )}
      </Row>

      {/* 结果表格 */}
      <Table
        columns={getColumns()}
        dataSource={getTableData()}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
        }}
        scroll={{ x: 800 }}
      />

      {/* 详情模态框 */}
      <SequenceDetailModal
        visible={detailModalVisible}
        onClose={() => setDetailModalVisible(false)}
        data={currentItem}
        type={resultType}
      />
    </div>
  );

  return (
    <BaseResultDisplay
      title={title || getResultTypeTitle()}
      content={content}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      resultIndex={resultIndex}
    />
  );
};

export default BatchSequenceResult;